import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { SERVICE_NOTICE_EVENT } from '../lib/api';
import { openLiveChat } from './liveChatConfig';

/**
 * Hizmetin geçici olarak kapalı olduğunu anlatan pencere.
 *
 * Backend, kapalı bir işleme gelen isteği ``X-Service-Notice`` başlığıyla
 * reddeder (bkz. backend/services/license.py); istemci bunu sıradan bir yetki
 * hatasından ayırıp burada gösterir. Küçük bir bildirim balonu yeterli
 * değildir: oyuncu oyunun neden açılmadığını, parasına ne olduğunu ve ne
 * yapabileceğini görmelidir — aksi hâlde şikâyet "site bozuldu" diye gelir.
 *
 * Sebep bilinçli olarak yazılmaz (lisans, sağlayıcı, bakım…): oyuncuya işletme
 * bilgisi verilmez, gerekçe panelde ve logdadır.
 */
const ServiceNoticeModal = () => {
  const [notice, setNotice] = useState(null);
  const [chatReady, setChatReady] = useState(false);

  const close = useCallback(() => setNotice(null), []);

  useEffect(() => {
    const onNotice = (event) => {
      setNotice({
        message: event.detail?.message
          || 'Bu işlem şu anda kullanılamıyor.',
      });
      // Widget yüklü değilken düğme sessizce hiçbir şey yapmaz; durum her
      // açılışta yeniden okunur çünkü canlı destek sonradan yüklenebilir.
      setChatReady(typeof window.LiveChatWidget?.call === 'function');
    };
    window.addEventListener(SERVICE_NOTICE_EVENT, onNotice);
    return () => window.removeEventListener(SERVICE_NOTICE_EVENT, onNotice);
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notice, close]);

  if (!notice) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="service-notice-title"
      onClick={close}
    >
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      />
      <div
        className="relative w-full max-w-md pusu-panel p-6 md:p-7"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Kapat"
          className="absolute top-4 right-4 side-icon-btn inline-flex"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
            style={{ background: 'rgba(245, 158, 11, 0.14)', color: '#fbbf24' }}
          >
            <AlertTriangle size={22} />
          </span>
          <div className="min-w-0">
            <h2 id="service-notice-title" className="text-lg font-bold">
              Bu hizmet şu anda kapalı
            </h2>
            <p className="mt-1 text-sm text-gray-300">{notice.message}</p>
          </div>
        </div>

        <div
          className="mt-5 rounded-xl border p-3 text-sm text-gray-300"
          style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}
        >
          <strong>Hesabınız ve bakiyeniz güvende.</strong> Hizmet yeniden
          açıldığında kaldığınız yerden devam edebilirsiniz.
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" onClick={close} className="gold-btn flex-1 py-2.5 rounded-xl font-semibold">
            Tamam
          </button>
          {chatReady && (
            <button
              type="button"
              className="flex-1 py-2.5 rounded-xl font-semibold border"
              style={{ borderColor: 'rgba(255,255,255,0.12)' }}
              onClick={() => { openLiveChat(); close(); }}
            >
              Canlı Destek
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceNoticeModal;
